import get from 'lodash/get';
import { UserStatus } from 'tccc-utils';
import { getLogger } from '../common/Logger';
import { MSG_EVENTS } from '../constants/trtc';
import type { TcccSdk } from '../tccc';
import {
  IMCallInInvite,
  inviteCallStateChanged,
  receiveStateChanged,
  asrEvent,
  IMHangup,
  IMOnlineStateChanged,
} from './s2cHandler';
import { UseMobileCallOut } from '../constants/sessions';
// import { tracer } from '../http/tracer';
import { SessionManager, SessionSummary } from './session-manager/session-manager';
import { TcccWebsocket } from './tcccWebsocket';
import { checkSessionSeq } from './session-manager/checkSession';
import { AgentStateType, emitAgentState, emitUserSettings } from '../tccc/Agent';

export type SocketMessage = {
  errorCode: string;
  msg: string;
  nonce: string;
  event?: string;
  command?: 's2c';
  sessionSummaryList?: null | SessionSummary[];
} & Omit<AgentStateType, 'status'> & { status: string } & Partial<{
    useMobileAccept: 0 | 1 | 2;
    useMobileCallOut: boolean;
  }>;

export class RtcSocket {
  userId: string;
  sdkAppId: string;
  emitter: TcccSdk;
  running: boolean;
  eventQueue: SocketMessage[];
  ws: TcccWebsocket;
  logger: ReturnType<typeof getLogger>;

  constructor(emitter: TcccSdk) {
    if (!emitter.Agent.userInfo) {
      throw new Error('Authorization failed');
    }
    this.emitter = emitter;
    this.userId = emitter.Agent.userInfo.userId;
    this.sdkAppId = emitter.Agent.userInfo.sdkAppId;
    this.logger = getLogger(emitter.Agent.userInfo);
    this.ws = new TcccWebsocket({ sdkAppId: this.sdkAppId, userId: this.userId });
    this.ws.on('heartbeat', (e) => {
      const state: AgentStateType = {
        status: +e.status,
        logicState: e.logicState,
        logicStatus: e.logicStatus,
        reason: e.reason,
        IM: e.IM,
        queueCount: e.queueCount,
      };
      emitAgentState(this.emitter, state);
      if ('useMobileAccept' in e && 'useMobileCallOut' in e) {
        emitUserSettings(this.emitter, {
          useMobileAcceptType: e.useMobileAccept,
          useMobileCallOutType: e.useMobileCallOut ? UseMobileCallOut.on : UseMobileCallOut.off,
        });
      }
      if (this.userId) {
        checkSessionSeq(this.userId, this.emitter, e.sessionSummaryList);
      } else {
        this.logger.error('Internal Error in heartbeat check');
      }
    });
    this.ws.on('connect', () => {
      this.emitter.emit('connectionStateChanged', { state: 'connected' });
    });
    this.ws.on('disconnect', () => {
      this.emitter.emit('connectionStateChanged', { state: 'disconnected' });
    });
    this.ws.on('s2c', (message) => {
      this.handleMessage(message);
    });
    this.eventQueue = [];
    this.running = false;
  }
  handleMessage(event: SocketMessage) {
    const data = event;
    const MsgEvent = get(data, 'event');
    if (data.errorCode && [MSG_EVENTS.OptRedisFailed, MSG_EVENTS.SetStatusConflict].includes(data.errorCode)) {
      return;
    }
    if (data.errorCode && data.errorCode !== '0') {
      this.logger.error(`socket response error ${data.errorCode}`);
      return;
    }
    // isTestEnv && console.log('[INFO] [WS] Message from server ', event.data);
    if (data.command === 's2c') {
      if (data.event && ![MSG_EVENTS.AsrResult].includes(data.event)) {
        this.logger.info(`s2c response,`, `nonce: ${data.nonce}, event: ${data.event}`);
      }
    }
    if (MsgEvent) {
      this.enqueueEvent(data);
    }
  }

  enqueueEvent(data: SocketMessage) {
    if (data.event && [MSG_EVENTS.AsrResult].includes(data.event)) {
      return this.onHandleDataEvent(data);
    }
    this.eventQueue.push(data);
    if (this.running) return;
    this.consumeEvent();
  }
  async consumeEvent() {
    this.running = true;
    if (this.eventQueue.length > 0) {
      const data = this.eventQueue.shift();
      try {
        await this.onHandleDataEvent(data);
      } catch (e) {
        this.logger.error('s2c handle failed', e);
      } finally {
        await this.consumeEvent();
      }
    } else {
      this.running = false;
    }
  }

  async onHandleDataEvent(data: any) {
    // 1: 电话呼入邀请，attendee是主叫 2: 通知客户座席信息，attendee是座席信息 3: 对方振铃 4: 对方接听 5: 对方挂断 8: im接入 7 视频接入 9 音频接入 100:没有心跳断线 101:多处登录踢下线
    const MsgEvent = data.event;
    if (MsgEvent && ![MSG_EVENTS.AsrResult].includes(MsgEvent)) {
      this.logger.info('Message from Server', data);
    }

    if (MsgEvent === MSG_EVENTS.UpdateAgentState) {
      const { newStatus } = data;
      const changed: AgentStateType = {
        status: +newStatus.status,
        logicState: newStatus.logicState,
        logicStatus: newStatus.logicStatus,
        reason: newStatus.reason,
        IM: newStatus.IM,
      };
      emitAgentState(this.emitter, changed);
    }

    if (MSG_EVENTS.IMHangUp === MsgEvent) {
      return IMHangup(data, this.emitter);
    }
    // 强制下线事件
    if (MsgEvent === MSG_EVENTS.ForceOffline) {
      this.emitter.emit('forcedOffline');
      this.logout('forcedOffLine');
      return;
    }
    if (MsgEvent === MSG_EVENTS.OfflineMultipleLogin) {
      this.emitter.emit('kickedOut');
      this.logout('kickedOut');
      return;
    }
    // IM 接入
    if (MsgEvent === MSG_EVENTS.ImCallIn) {
      return IMCallInInvite(data, this.emitter);
    }
    if (MsgEvent === MSG_EVENTS.BroadcastForwardAnswered) {
      return inviteCallStateChanged({ ...data, state: 'accepted' }, this.emitter);
    }
    if (MsgEvent === MSG_EVENTS.BroadcastForwardHangup) {
      return inviteCallStateChanged({ ...data, state: 'hangup' }, this.emitter);
    }
    if (MsgEvent === MSG_EVENTS.BroadcastForwardFailed) {
      return inviteCallStateChanged({ ...data, state: 'failed' }, this.emitter);
    }
    if (MsgEvent === MSG_EVENTS.InputCallback) {
      return receiveStateChanged(data, this.emitter);
    }
    if (MsgEvent === MSG_EVENTS.MemberListChange) {
      if (this.userId) {
        SessionManager.create(this.userId, data, this.emitter);
      }
      return;
    }

    if (MsgEvent === MSG_EVENTS.ImStateChange) {
      return IMOnlineStateChanged(data, this.emitter);
    }
    if (MsgEvent === MSG_EVENTS.AsrResult) {
      return asrEvent(data, this.emitter);
    }
  }
  login({ sdkAppId, userId, sessionKey }: { sdkAppId: string; userId: string; sessionKey: string }) {
    return new Promise<void>((resolve, reject) => {
      // login success
      this.ws.once('connect', resolve);
      // 连续5次连接失败报错
      this.ws.once('failed', reject);
      this.userId = userId;
      this.sdkAppId = sdkAppId;

      return this.ws
        .login({ sdkAppId, userId, sessionKey })
        .then(() => {
          resolve();
        })
        .catch((e) => {
          reject(e);
        });
    });
  }
  setStatus<T extends keyof typeof UserStatus>(status: T, restReason?: string): Promise<SocketMessage> {
    return this.ws.setStatus(status, restReason);
  }
  logout(force?: string) {
    this.ws.logout(force);
    const state: AgentStateType = {
      status: 0,
      logicState: 'idle',
      logicStatus: 'offline',
      reason: '',
      IM: {
        total: 0,
        used: 0,
      },
      queueCount: {},
    };
    if (force) {
      emitAgentState(this.emitter, state);
    }
    this.emitter.emit('connectionStateChanged', { state: 'disconnected' });
  }
}
